// Generated levels per item (DEPTH.md §2) and reading positions (§6), in IndexedDB.
import * as db from "../store/db";
import { boldTerms } from "./markup";
import type { Structure } from "./types";
import { Hub } from "../util";

export interface ReadingPosition { chapter: number; paragraph: number; offset?: number; level: number; updatedAt: number }

interface Item { structures: Record<string, Structure>; notes: Record<string, string> }

class LevelsStore {
  private items = new Map<string, Item>();
  private positions = new Map<string, ReadingPosition | null>();
  private loading = new Map<string, Promise<void>>();
  private saveTimers = new Map<string, ReturnType<typeof setTimeout>>();
  readonly events = new Hub<{ changed: { key: string; chapter?: number; beat?: number; partial?: boolean } }>();
  /** Generated here (not applied from sync): DEPTH.md §8 pushes these. */
  onStructureGenerated: (key: string, chapter: number) => void = () => {};
  onNoteGenerated: (key: string, chapter: number) => void = () => {};
  onCleared: (key: string) => void = () => {};

  load(key: string): Promise<void> {
    if (this.items.has(key)) return Promise.resolve();
    let p = this.loading.get(key);
    if (!p) {
      p = (async () => {
        const [item, pos] = await Promise.all([db.get<Item>("levels", key).catch(() => undefined), db.get<ReadingPosition>("positions", key).catch(() => undefined)]);
        if (!this.items.has(key)) this.items.set(key, item ?? { structures: {}, notes: {} });
        if (!this.positions.has(key)) this.positions.set(key, pos ?? null);
      })();
      this.loading.set(key, p);
    }
    return p;
  }

  isLoaded(key: string) { return this.items.has(key); }
  private item(key: string): Item {
    let i = this.items.get(key);
    if (!i) { i = { structures: {}, notes: {} }; this.items.set(key, i); }
    return i;
  }

  structure(key: string, chapter: number): Structure | null { return this.items.get(key)?.structures[chapter] ?? null; }
  note(key: string, chapter: number, beat: number): string | null { return this.items.get(key)?.notes[`${chapter}:${beat}`] ?? null; }

  notes(key: string, chapter: number): Record<number, string> {
    const out: Record<number, string> = {};
    const prefix = `${chapter}:`;
    for (const [k, v] of Object.entries(this.items.get(key)?.notes ?? {})) if (k.startsWith(prefix)) out[parseInt(k.slice(prefix.length), 10)] = v;
    return out;
  }

  explainedTerms(key: string, chapter: number, before: number): string[] {
    const terms: string[] = [];
    for (let b = 0; b < before; b++) { const n = this.note(key, chapter, b); if (n) terms.push(...boldTerms(n)); }
    const seen = new Set<string>();
    return terms.filter((t) => t && !seen.has(t) && (seen.add(t), true));
  }

  setStructure(s: Structure, key: string, chapter: number): void {
    this.item(key).structures[chapter] = s;
    this.save(key);
    this.events.emit("changed", { key, chapter });
    this.onStructureGenerated(key, chapter);
  }

  setNote(text: string, key: string, chapter: number, beat: number): void {
    this.item(key).notes[`${chapter}:${beat}`] = text;
    this.save(key);
    this.events.emit("changed", { key, chapter, beat });
    this.onNoteGenerated(key, chapter);
  }

  /** A chapter as synced from another device (DEPTH.md §8): replaces its structure and notes. Not pushed back. */
  applySynced(s: Structure, chapterNotes: Record<number, string>, key: string, chapter: number): void {
    const it = this.item(key);
    it.structures[chapter] = s;
    const prefix = `${chapter}:`;
    for (const k of Object.keys(it.notes)) if (k.startsWith(prefix)) delete it.notes[k];
    for (const [b, n] of Object.entries(chapterNotes)) it.notes[`${chapter}:${b}`] = n;
    this.save(key);
    this.events.emit("changed", { key, chapter });
  }

  clear(key: string): void {
    this.items.set(key, { structures: {}, notes: {} });
    this.save(key, true);
    this.onCleared(key);
    this.events.emit("changed", { key });
  }

  position(key: string): ReadingPosition | null { return this.positions.get(key) ?? null; }

  setPosition(p: Omit<ReadingPosition, "updatedAt">, key: string): void {
    const old = this.positions.get(key);
    if (old && old.chapter === p.chapter && old.paragraph === p.paragraph && (old.offset ?? 0) === (p.offset ?? 0) && old.level === p.level) return;
    const pos = { ...p, updatedAt: Date.now() };
    this.positions.set(key, pos);
    db.put("positions", key, pos).catch(() => {});
  }

  private save(key: string, now = false): void {
    clearTimeout(this.saveTimers.get(key));
    const write = () => { this.saveTimers.delete(key); db.put("levels", key, this.item(key)).catch(() => {}); };
    if (now) write(); else this.saveTimers.set(key, setTimeout(write, 500));
  }
}

export const levelsStore = new LevelsStore();
